import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";
import { SITE } from "@/lib/seo";
import jsonLd from "@/data/certificate-of-destruction-guide-jsonld.json";

/**
 * Dedicated article route modelled on the EU textile EPR and ICO Met Police news
 * routes. Copy is the approved article verbatim; JSON-LD is emitted exactly as
 * supplied in the data file.
 */

const SLUG = "certificate-of-destruction-guide";
const URL = `${SITE}/blog/${SLUG}`;
const TITLE_TAG = "Certificate of Destruction: A Business Guide | Preserve Earth";
const META_DESCRIPTION =
  "What a certificate of destruction is, what it should include, why your business needs one and how to file it so you can show what was destroyed.";
const H1 = "What is a certificate of destruction, and how should your business keep one?";
const HERO_SRC = "/images/blog/certificate-of-destruction-guide-hero.webp";
const OG_IMAGE = `${SITE}/images/blog/certificate-of-destruction-guide-og.jpg`;
const HERO_ALT =
  "Smiling office manager at a bright desk checking a one-page document on a clipboard beside a laptop and a tabbed binder";
const DATE = "2026-11-02";

const crumbs = [
  { name: "Blog", path: "/blog" },
  { name: H1, path: `/blog/${SLUG}` },
];

const SOURCES = [
  "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/",
  "https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security",
  "https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/contracts-and-liabilities-between-controllers-and-processors-multi/what-needs-to-be-included-in-the-contract/",
  "https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/data-protection-principles/a-guide-to-the-data-protection-principles/storage-limitation/",
];

export const Route = createFileRoute("/blog/certificate-of-destruction-guide")({
  head: () => ({
    meta: [
      { title: TITLE_TAG },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: TITLE_TAG },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: HERO_ALT },
      { property: "article:published_time", content: DATE },
      { property: "article:modified_time", content: DATE },
      { property: "article:section", content: "Data Protection" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE_TAG },
      { name: "twitter:description", content: META_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: HERO_ALT },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: (jsonLd as object[]).map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    })),
  }),
  component: CertificateOfDestructionGuidePage,
});

const linkClass =
  "underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss";
const h2Class = "mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl";
const h3Class = "mt-10 font-serif text-xl leading-snug text-moss";
const bodyClass = "mt-5 text-pretty text-base leading-[1.8] text-charcoal/80";
const listClass =
  "mt-6 list-disc space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss";

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={linkClass}>
      {children}
    </a>
  );
}

function CertificateOfDestructionGuidePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={crumbs} />
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              Data Protection · 6 min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {H1}
            </h1>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-charcoal/70">
              By Preserve Earth team, Last updated <time dateTime={DATE}>2 November 2026</time>
            </p>
          </header>

          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                width={1280}
                height={720}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                <p className="text-pretty text-lg leading-[1.75] text-charcoal">
                  <strong>Short answer:</strong> A certificate of destruction is a written record
                  from whoever destroyed your confidential paper, devices or stock, confirming what
                  was destroyed and when. It is your evidence that information left your control and
                  was dealt with securely. The ICO expects organisations that use an outside
                  provider to get that kind of assurance, check it against what they sent, and keep
                  it on file.
                </p>

                <section id="section-1" aria-labelledby="section-1-h">
                  <h2 id="section-1-h" className={h2Class}>
                    What is a certificate of destruction?
                  </h2>
                  <p className={bodyClass}>
                    When you hand confidential material to someone else to destroy, you lose sight
                    of it the moment the bin, sack or crate leaves the building. A certificate of
                    destruction closes that gap. It is a document, usually issued electronically,
                    that confirms a specific collection was received and securely destroyed.
                  </p>
                  <p className={bodyClass}>
                    You may see it called a destruction certificate, a proof of destruction or a
                    note of secure destruction. The name matters less than the content. What you
                    want is a record you can match to a particular collection from a particular site
                    on a particular day.
                  </p>
                  <p className={bodyClass}>
                    It applies to more than paper. The same idea covers hard drives, laptops and
                    other media, and branded stock or uniforms that you do not want resold or
                    reused.
                  </p>
                </section>

                <section id="section-2" aria-labelledby="section-2-h">
                  <h2 id="section-2-h" className={h2Class}>
                    Why does your business need one?
                  </h2>
                  <h3 className={h3Class}>UK GDPR expects you to show your working</h3>
                  <p className={bodyClass}>
                    Under UK GDPR you are responsible for keeping personal data secure, and you must
                    be able to demonstrate how you do it. Disposal is part of that. The ICO's{" "}
                    <A href="https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/disposal-and-deletion/">
                      disposal and deletion toolkit
                    </A>{" "}
                    says organisations using a third party should have contracts that give
                    appropriate assurance of secure disposal, for example through audit checks and
                    destruction certificates. It also suggests that a member of staff checks that
                    certificates match what was sent for destruction.
                  </p>
                  <p className={bodyClass}>
                    Without evidence of disposal, the ICO warns that personal information may not be
                    secure during disposal, or may not be disposed of at all.
                  </p>
                  <h3 className={h3Class}>It belongs in your supplier arrangements</h3>
                  <p className={bodyClass}>
                    If a provider destroys personal data for you, it is acting on your behalf. The
                    ICO explains{" "}
                    <A href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/contracts-and-liabilities-between-controllers-and-processors-multi/what-needs-to-be-included-in-the-contract/">
                      what a contract with a processor needs to include,
                    </A>{" "}
                    such as security measures and the information you need to show compliance.
                    Asking for a certificate for every job is a simple, practical way to get that
                    information.
                  </p>
                  <h3 className={h3Class}>It helps when someone asks questions later</h3>
                  <p className={bodyClass}>
                    Auditors, clients, insurers and your own management may ask how you handle
                    confidential waste. A certificate per collection, filed in one place, lets you
                    answer quickly and specifically rather than relying on memory.
                  </p>
                </section>

                <section id="section-3" aria-labelledby="section-3-h">
                  <h2 id="section-3-h" className={h2Class}>
                    What should a certificate of destruction contain?
                  </h2>
                  <p className={bodyClass}>
                    There is no single legal template, but a useful certificate lets you answer five
                    questions: what, how much, from where, when and by whom. Look for:
                  </p>
                  <ul className={listClass}>
                    <li>
                      <strong>Your organisation and site.</strong> The name and the address the
                      material was collected from.
                    </li>
                    <li>
                      <strong>Date of collection and date of destruction.</strong> These can be
                      different, and both are useful.
                    </li>
                    <li>
                      <strong>What was destroyed.</strong> For example confidential paper, hard
                      drives, laptops, mixed media or branded stock.
                    </li>
                    <li>
                      <strong>Quantity.</strong> Number of consoles or sacks, weight, or a count of
                      items. For IT equipment, serial numbers or asset tags where they were
                      recorded.
                    </li>
                    <li>
                      <strong>Method.</strong> For example shredding, or physical destruction of
                      drives.
                    </li>
                    <li>
                      <strong>A unique reference.</strong> So you can tie the certificate to a
                      booking, a collection note and your own log.
                    </li>
                    <li>
                      <strong>Confirmation of destruction.</strong> A clear statement that the
                      material listed has been securely destroyed.
                    </li>
                    <li>
                      <strong>Who issued it.</strong> The provider's name and contact details.
                    </li>
                  </ul>
                  <p className={bodyClass}>
                    If a certificate you receive is vague, for example "assorted waste" with no
                    quantity or date, ask for more detail before you file it.
                  </p>
                </section>

                <section id="section-4" aria-labelledby="section-4-h">
                  <h2 id="section-4-h" className={h2Class}>
                    How we handle it
                  </h2>
                  <p className={bodyClass}>
                    For confidential waste collections, we issue an electronic note of secure
                    destruction the working day after collection. It details the quantity and type
                    of waste collected and confirms it has been securely destroyed. For IT and
                    media, we provide chain of custody and destruction documentation, so you have an
                    audit trail for your records. You can read more on our{" "}
                    <A href="https://preserveearth.co.uk/services/confidential-waste-disposal">
                      confidential waste disposal
                    </A>{" "}
                    and{" "}
                    <A href="https://preserveearth.co.uk/services/it-and-media-recycling">
                      IT and media recycling
                    </A>{" "}
                    pages.
                  </p>
                </section>

                <section id="section-5" aria-labelledby="section-5-h">
                  <h2 id="section-5-h" className={h2Class}>
                    How should you keep your records?
                  </h2>
                  <h3 className={h3Class}>Keep a disposal log</h3>
                  <p className={bodyClass}>
                    The ICO expects a log of equipment and confidential waste sent for disposal or
                    destruction, and a log of records and devices awaiting destruction and where
                    they are. A spreadsheet is enough for most offices. Record the date, the type
                    and quantity of material, the site, who authorised it and the collection
                    reference.
                  </p>
                  <h3 className={h3Class}>Match every certificate to the log</h3>
                  <p className={bodyClass}>
                    When the certificate arrives, check it against your log entry. Does the quantity
                    agree? Is the date right? Is anything missing? Note the certificate reference
                    next to the log entry and the name of the person who checked it.
                  </p>
                  <h3 className={h3Class}>Store certificates in one dedicated place</h3>
                  <p className={bodyClass}>
                    The ICO suggests keeping electronic confirmations of destruction in a dedicated
                    folder or email inbox. Pick one location, restrict who can edit it, and name
                    files consistently, for example by date and site.
                  </p>
                  <h3 className={h3Class}>Decide how long to keep them</h3>
                  <p className={bodyClass}>
                    UK GDPR's{" "}
                    <A href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/data-protection-principles/a-guide-to-the-data-protection-principles/storage-limitation/">
                      storage limitation principle
                    </A>{" "}
                    applies to the personal data itself, but your certificates are business records
                    too. Set a retention period for them in your retention schedule, based on how
                    long you might need to evidence disposal, and review it alongside your other
                    records.
                  </p>
                  <h3 className={h3Class}>Approve destruction before it happens</h3>
                  <p className={bodyClass}>
                    The ICO's{" "}
                    <A href="https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/accountability/records-management-and-security">
                      records management and security toolkit
                    </A>{" "}
                    and its disposal guidance both point to destroying records in line with a
                    retention schedule, with management approval logged before destruction. The
                    certificate then becomes the final step in a chain that starts with a decision
                    you can point to.
                  </p>
                </section>

                <section id="section-6" aria-labelledby="section-6-h">
                  <h2 id="section-6-h" className={h2Class}>
                    Common gaps to watch for
                  </h2>
                  <ul className={listClass}>
                    <li>Certificates arriving by email and sitting in one person's inbox.</li>
                    <li>
                      One certificate covering several sites, with no way to tell which material
                      came from where.
                    </li>
                    <li>
                      IT equipment listed only as "laptops x 20" when you need serial numbers to
                      close off your asset register.
                    </li>
                    <li>
                      No one checking that the certificate matches what actually left the building.
                    </li>
                  </ul>
                  <p className={bodyClass}>
                    Each of these is easy to fix with a named owner and a monthly ten-minute check.
                  </p>
                  <p className={bodyClass}>
                    For a wider look at what the ICO expects across paper and devices, see our{" "}
                    <A href="https://preserveearth.co.uk/blog/ico-confidential-waste-disposal-guide">
                      ICO confidential waste disposal guide.
                    </A>{" "}
                    If you are reviewing who destroys your waste, our piece on{" "}
                    <A href="https://preserveearth.co.uk/blog/ico-met-police-third-party-suppliers-disposal">
                      choosing third-party suppliers
                    </A>{" "}
                    covers the checks worth making.
                  </p>
                </section>

                <section id="faq" aria-labelledby="faq-h">
                  <h2 id="faq-h" className={h2Class}>
                    Frequently asked questions
                  </h2>
                  <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Is a certificate of destruction a legal requirement?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        There is no single legal form, but UK GDPR expects you to keep personal data
                        secure and to be able to show how you do it. The ICO says contracts with
                        disposal providers should give assurance of secure disposal, for example
                        through destruction certificates.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        What should a certificate of destruction include?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Your organisation and site, collection and destruction dates, what was
                        destroyed, the quantity, the method, a unique reference, a confirmation of
                        destruction and the name of the provider that issued it.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        How long should we keep certificates of destruction?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Set a period in your own retention schedule based on how long you might need
                        to evidence disposal, and review it with your other records. There is no
                        single period that fits every organisation.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Do we get a certificate for IT equipment as well as paper?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        You should ask for one. For IT and media we provide chain of custody and
                        destruction documentation, and serial numbers or asset tags make it much
                        easier to close off your asset register.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Who in the business should check certificates?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Name one person. The ICO suggests a staff member checks that certificates
                        match what was sent for destruction, and records that the check was done.
                      </p>
                    </div>
                  </div>
                </section>

                <aside
                  aria-labelledby="cta-h"
                  className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10"
                >
                  <h2 id="cta-h" className="font-serif text-2xl text-charcoal">
                    Need confidential waste or IT equipment collected?
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    Tell us what you have and where it is, and we will come back to you with the
                    right collection option and the paperwork you will receive.
                  </p>
                  <a
                    href="https://preserveearth.co.uk/services/confidential-waste-disposal#enquire"
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    Get in touch
                  </a>
                </aside>

                <div className="mt-12 text-sm leading-relaxed text-charcoal/70">
                  <h2 className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    Sources
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {SOURCES.map((href) => (
                      <li key={href}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener"
                          className={`${linkClass} break-all`}
                        >
                          {href}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>

        <RelatedArticles
          slugs={[
            "ico-confidential-waste-disposal-guide",
            "confidential-paper-binned-by-mistake-zagreb-lesson",
          ]}
        />
        <RelatedServices
          slugs={["confidential-waste-disposal", "it-and-media-recycling", "one-off-shredding"]}
        />
      </main>

      <SiteFooter />
    </div>
  );
}
